// frontend/client/src/HomePage.js
import React, { useEffect, useState } from "react";
import "./CampusFeedPage.css"; // af-* base styles FIRST
import "./HomePage.css"; // overrides LAST
import { useAuth } from "./auth";
import FeedsCore, { AvatarWithFallback } from "./FeedsCore";
import { PenTool, Binoculars } from "lucide-react";

/* Inline icons (no deps) */
function IconSearch(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <circle cx="9" cy="9" r="4.5" strokeWidth="2.5" />
      <line x1="22" y1="22" x2="12.2" y2="12.2" strokeWidth="2.5" />
    </svg>
  );
}

function IconPen(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path d="M12 20h9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function HomePage() {
  const { user } = useAuth();

  // Panels (same page)
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Feeds state
  const [activeTab, setActiveTab] = useState("campus"); // "campus" | "anonymous"
  const [openPostIdByTab, setOpenPostIdByTab] = useState({
    campus: null,
    anonymous: null,
  });

  // mobile detect
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // aktif taba göre “o tabın” openPostId’si
  const openPostId = openPostIdByTab[activeTab] ?? null;

  const setOpenPostId = (next) => {
    setOpenPostIdByTab((prev) => ({
      ...prev,
      [activeTab]: typeof next === "function" ? next(prev[activeTab]) : next,
    }));
  };

  const [composeOpen, setComposeOpen] = useState(false);
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);

  // CSRF token for follow actions
  const [csrfToken, setCsrfToken] = useState("");

  // dashboard counts
  const [today, setToday] = useState({ campusPosts: 0, anonymousPosts: 0 });
  const [todayLoading, setTodayLoading] = useState(true);

  // social query + results
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchUsers, setSearchUsers] = useState([]);
  const [searchError, setSearchError] = useState("");

  // follow button in search list
  const [followBusyId, setFollowBusyId] = useState(null);

  const go = (hashPath) => {
    window.location.href = `/react#${hashPath.startsWith("/") ? hashPath : `/${hashPath}`}`;
  };

  async function sendFollowRequest(userId) {
    if (!userId) return;
    if (followBusyId) return;

    setFollowBusyId(userId);
    try {
      const res = await fetch("/api/follow-request", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, csrfToken }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      setSearchUsers((prev) =>
        prev.map((u) =>
          String(u?.id) === String(userId) ? { ...u, relationship: "REQUEST_SENT" } : u
        )
      );
    } catch (e) {
      console.error("sendFollowRequest failed", e);
      setSearchError("Could not send follow request");
    } finally {
      setFollowBusyId(null);
    }
  }

  async function runSearch(term) {
    const t = (term || "").trim();
    if (!t) {
      setSearchUsers([]);
      setSearchError("");
      return;
    }

    window.location.hash = `/dashboard?panel=search&q=${encodeURIComponent(t)}`;

    setSearchLoading(true);
    setSearchError("");

    try {
      const res = await fetch(`/api/search-profiles?q=${encodeURIComponent(t)}`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => null);

      if (!res.ok || !data || !data.ok) {
        setSearchUsers([]);
        setSearchError((data && data.error) || `HTTP ${res.status}`);
        return;
      }

      setSearchUsers(Array.isArray(data.users) ? data.users : []);
    } catch {
      setSearchUsers([]);
      setSearchError("Network error");
    } finally {
      setSearchLoading(false);
    }
  }

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/dashboard", { credentials: "include" });
        const data = await res.json().catch(() => null);
        if (!alive) return;
        if (res.ok && data?.ok && data.today) {
          setToday({
            campusPosts: Number(data.today.campusPosts) || 0,
            anonymousPosts: Number(data.today.anonymousPosts) || 0,
          });
        }
        if (data?.csrfToken) setCsrfToken(String(data.csrfToken));
      } catch {
        // ignore
      } finally {
        if (alive) setTodayLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash || "";
    const qIndex = hash.indexOf("?");
    const qs = qIndex >= 0 ? hash.slice(qIndex + 1) : "";
    const params = new URLSearchParams(qs);

    if (params.get("panel") === "search") {
      setIsSearchOpen(true);
      setComposeOpen(false);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash || "";
    const qIndex = hash.indexOf("?");
    const qs = qIndex >= 0 ? hash.slice(qIndex + 1) : "";
    const params = new URLSearchParams(qs);

    const panel = params.get("panel");
    const q = params.get("q") || "";

    if (panel === "search") {
      setIsSearchOpen(true);
      setComposeOpen(false);
    }

    if (q) {
      setSearchQuery(q);
      runSearch(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search kapanınca hash'i temizle — yoksa yenilemede tekrar search açılıyor
  useEffect(() => {
    if (!isSearchOpen && window.location.hash.includes("panel=search")) {
      window.location.hash = "/dashboard";
    }
  }, [isSearchOpen]);

  // Profil sayfasından gelen scrollTo parametresi — post'a scroll et
  useEffect(() => {
    const hash = window.location.hash || "";
    const qIndex = hash.indexOf("?");
    if (qIndex < 0) return;
    const params = new URLSearchParams(hash.slice(qIndex + 1));
    const scrollTo = params.get("scrollTo");
    if (!scrollTo) return;

    // Feed yüklenene kadar bekle, sonra scroll et
    const timer = setInterval(() => {
      const el = document.getElementById(scrollTo);
      if (el) {
        clearInterval(timer);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Hash'ten scrollTo'yu temizle
        window.location.hash = "/dashboard";
      }
    }, 200);

    // 5 saniye sonra vazgeç
    const timeout = setTimeout(() => clearInterval(timer), 5000);
    return () => { clearInterval(timer); clearTimeout(timeout); };
  }, []);

  useEffect(() => {
    setComposeOpen(false);
  }, [activeTab]);

  const profileName = user?.fullName || user?.name || "?";

  const campusCount = todayLoading ? "…" : String(today.campusPosts);
  const freeCount = todayLoading ? "…" : String(today.anonymousPosts);

  const rightIcon = composeOpen ? "binoculars" : "pentool";

  return (
    <div className="hm-page">
      <div className="hm-topblock">
        <div className="hm-header">
          <h1 className="hm-title">Home</h1>

          <div className="hm-nav">
            <button
              onClick={() => {
                setIsSearchOpen((v) => {
                  const next = !v;
                  if (next) setComposeOpen(false);
                  return next;
                });
              }}
              className={`hm-iconbtn ${isSearchOpen ? "hm-iconbtn--active" : ""}`}
              title="Find friends"
              type="button"
            >
              <IconSearch className="hm-ico" />
            </button>

            <button
              onClick={() => {
                setIsSearchOpen(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`hm-iconbtn ${!isSearchOpen ? "hm-iconbtn--active" : ""}`}
              title="Feeds"
              type="button"
            >
              <IconPen className="hm-ico" />
            </button>
          </div>

          <button
            className="hm-profile"
            type="button"
            title="Profile"
            onClick={() => go("/profile")}
          >
            <AvatarWithFallback
              src={
                user?.profileImageUrl && String(user.profileImageUrl).trim()
                  ? user.profileImageUrl
                  : ""
              }
              name={profileName}
              className="hm-profile-img"
            />
          </button>
        </div>

        {!isSearchOpen && (
          <div className="hm-tabsbar">
            <div className="hm-tabs">
              <button
                type="button"
                onClick={() => setActiveTab("campus")}
                className={`hm-tab ${activeTab === "campus" ? "hm-tab--active" : ""}`}
              >
                campus <span className="hm-badge">{campusCount}</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("anonymous")}
                className={`hm-tab ${activeTab === "anonymous" ? "hm-tab--active" : ""}`}
              >
                <span className="hm-mask">🎭</span> free <span className="hm-badge">{freeCount}</span>
              </button>
            </div>

            <button
              className={`hm-create ${composeOpen ? "hm-create--active" : ""}`}
              type="button"
              title={composeOpen ? "View feed" : "Compose"}
              onClick={() => {
                setIsSearchOpen(false);
                setComposeOpen((v) => !v);
              }}
            >
              {rightIcon === "binoculars" ? (
                <Binoculars className="hm-ico2" />
              ) : (
                <PenTool className="hm-ico2" />
              )}
            </button>
          </div>
        )}

        {!isSearchOpen && composeOpen && (
          <div className="hm-topblock-compose">
            <FeedsCore
              user={user}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              composeOpen={composeOpen}
              showTopbar={false}
              openPostId={openPostId}
              setOpenPostId={setOpenPostId}
              campusViewMode="flow"
              isMobile={isMobile}
              renderComposeOnly={true}
              onPostCreated={() => setFeedRefreshKey((k) => k + 1)}
              onGoProfile={() => go("/profile")}
              onGoDashboard={() => go("/dashboard")}
            />
          </div>
        )}
      </div>

      <div className="hm-wrap">
        <section className={`hm-panel ${isSearchOpen ? "hm-panel--open" : "hm-panel--closed"}`}>
          <h2 className="hm-h2">find friends</h2>

          <div className="hm-card">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                runSearch(searchQuery);
              }}
            >
              <div className="hm-label">Search profiles</div>

              <input
                className="hm-input"
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <div className="hm-row">
                <button className="hm-btn hm-btn--primary" type="submit" disabled={searchLoading}>
                  {searchLoading ? "Searching..." : "Search"}
                </button>

                <button className="hm-btn" type="button" onClick={() => setIsSearchOpen(false)}>
                  Back
                </button>
              </div>
            </form>

            {searchError && (
              <div style={{ marginTop: 10, color: "rgba(255, 180, 180, 0.95)", fontSize: 13 }}>
                {searchError}
              </div>
            )}

            {!searchError &&
              !searchLoading &&
              searchQuery.trim() !== "" &&
              searchUsers.length === 0 && (
                <div style={{ marginTop: 10, color: "rgba(235,229,217,0.82)", fontSize: 13 }}>
                  No users found.
                </div>
              )}

            {searchUsers.length > 0 && (
              <div
                className="hm-searchlist"
                style={{
                  marginTop: 12,
                  maxHeight: 260,
                  overflowY: "auto",
                  paddingRight: 6,
                }}
              >
                {searchUsers.map((u) => {
                  const id = u?.id;

                  const rel = String(u?.relationship || "NONE");
                  const isSent = rel === "REQUEST_SENT";
                  const isFollowing = rel === "FOLLOWING" || rel === "MUTUAL_FOLLOW";
                  const isReceived = rel === "REQUEST_RECEIVED";

                  const showTick = isSent || isFollowing || isReceived;
                  const disabledPlus = !id || followBusyId === id || showTick;

                  const plusTitle = isFollowing
                    ? "Already following"
                    : isSent
                    ? "Request sent"
                    : isReceived
                    ? "Request received"
                    : "Send follow request";

                  return (
                    <div
                      key={String(id ?? `${u?.fullName}-${u?.university}`)}
                      style={{
                        padding: "12px 0",
                        borderTop: "1px solid rgba(235,229,217,0.12)",
                      }}
                    >
                      <div style={{ fontWeight: 900, fontSize: 13, color: "rgba(235,229,217,0.92)" }}>
                        {u?.fullName || "User"}
                      </div>

                      <div style={{ fontSize: 12, color: "rgba(235,229,217,0.70)", marginTop: 2 }}>
                        {u?.university || ""}
                      </div>

                      <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center" }}>
                        <button
                          type="button"
                          className="hm-btn"
                          onClick={() => {
                            if (id) {
                              go(
                                `/user-profile?id=${encodeURIComponent(String(id))}&source=home-search&q=${encodeURIComponent(searchQuery)}`
                              );
                            }
                          }}
                          disabled={!id}
                        >
                          View profile
                        </button>

                        <button
                          type="button"
                          className={"hm-plusbtn" + (showTick ? " hm-plusbtn--sent" : "")}
                          title={plusTitle}
                          onClick={() => sendFollowRequest(id)}
                          disabled={disabledPlus}
                        >
                          {showTick ? "✓" : "+"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className={`hm-panel ${isSearchOpen ? "hm-panel--closed" : "hm-panel--open"}`}>
          <FeedsCore
            user={user}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            composeOpen={composeOpen}
            showTopbar={false}
            openPostId={openPostId}
            setOpenPostId={setOpenPostId}
            campusViewMode="flow"
            isMobile={isMobile}
            renderFeedOnly={true}
            refreshKey={feedRefreshKey}
            onGoProfile={() => go("/profile")}
            onGoDashboard={() => go("/dashboard")}
          />
        </section>
      </div>
    </div>
  );
}